// Configuración › Tipos de canal (shared/API.md §46) — system admins only. Each type is a section
// in everyone's list; the order here is the order there. Every write answers with the whole view, so
// the list below always shows what the server holds.
import { useState, useSyncExternalStore } from 'react';
import { ChannelMeta, type ChannelType } from '../../../core/channelMeta';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Button, ConfirmButton } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { writeChannelMeta } from '../channelPanel/ChannelMetaRows';
import { SettingsGroup } from './parts';

export function useChannelMetaState() {
  return useSyncExternalStore(ChannelMeta.subscribe, ChannelMeta.currentState);
}

function ColorSelect({ value, disabled, onChange }: { value: string; disabled?: boolean; onChange(color: string): void }) {
  return (
    <select className="text-input ct-color" value={value} disabled={disabled} aria-label={t('channelMeta.types.color')} onChange={(e) => onChange(e.target.value)}>
      {ChannelMeta.COLORS.map((c) => (
        <option key={c} value={c}>
          {t(`channelMeta.color.${c}`)}
        </option>
      ))}
    </select>
  );
}

function TypeRow({ type, index, count, busy, run }: { type: ChannelType; index: number; count: number; busy: boolean; run(op: Parameters<typeof writeChannelMeta>[0]): void }) {
  const [name, setName] = useState(type.name);
  const ids = ChannelMeta.currentState().types.map((x) => x.id);
  const move = (delta: number) => {
    const next = [...ids];
    const [moved] = next.splice(index, 1);
    next.splice(index + delta, 0, moved);
    run({ kind: 'orderTypes', ids: next });
  };
  const rename = () => {
    const clean = ChannelMeta.cleanName(name);
    if (!clean || clean === type.name) {
      setName(type.name);
      return;
    }
    run({ kind: 'updateType', typeId: type.id, patch: { name: clean } });
  };
  return (
    <div className="ct-row" data-channel-type-row={type.id}>
      <span className="channel-type-dot ct-dot" data-color={type.color} aria-hidden="true" />
      <input
        className="text-input ct-name"
        value={name}
        maxLength={ChannelMeta.MAX_TYPE_NAME}
        disabled={busy}
        aria-label={t('channelMeta.types.rename')}
        onChange={(e) => setName(e.target.value)}
        onBlur={rename}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          else if (e.key === 'Escape') setName(type.name);
        }}
      />
      <ColorSelect value={type.color} disabled={busy} onChange={(color) => run({ kind: 'updateType', typeId: type.id, patch: { color } })} />
      <button type="button" className="icon-button" disabled={busy || index === 0} title={t('channelMeta.types.moveUp')} aria-label={t('channelMeta.types.moveUp')} onClick={() => move(-1)}>
        <Icon name="chevronUp" size={15} />
      </button>
      <button type="button" className="icon-button" disabled={busy || index === count - 1} title={t('channelMeta.types.moveDown')} aria-label={t('channelMeta.types.moveDown')} onClick={() => move(1)}>
        <Icon name="chevronDown" size={15} />
      </button>
      <ConfirmButton icon="trash" label={t('channelMeta.types.delete')} confirmLabel={tf('channelMeta.types.deleteConfirm', type.name)} onConfirm={() => run({ kind: 'deleteType', typeId: type.id })} />
    </div>
  );
}

export function ChannelTypesSection() {
  const shell = useShell();
  const state = useChannelMetaState();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(ChannelMeta.COLORS[0]);

  const run = (op: Parameters<typeof writeChannelMeta>[0], after?: () => void) => {
    if (busy) return;
    setBusy(true);
    void writeChannelMeta(op, shell.toast).then((next) => {
      setBusy(false);
      if (next) after?.();
    });
  };
  const add = () => {
    const clean = ChannelMeta.cleanName(name);
    if (!clean) return;
    run({ kind: 'createType', name: clean, color }, () => setName(''));
  };

  return (
    <SettingsGroup footer={t('channelMeta.types.footer')}>
      <div className="ct-list">
        {state.types.length === 0 ? <div className="ct-empty">{t('channelMeta.types.empty')}</div> : null}
        {state.types.map((type, i) => (
          // keyed by name too: a rename from elsewhere resets the field
          <TypeRow key={`${type.id}:${type.name}`} type={type} index={i} count={state.types.length} busy={busy} run={(op) => run(op)} />
        ))}
        <form
          className="ct-row ct-add"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <span className="channel-type-dot ct-dot" data-color={color} aria-hidden="true" />
          <input
            className="text-input ct-name"
            value={name}
            maxLength={ChannelMeta.MAX_TYPE_NAME}
            disabled={busy}
            placeholder={t('channelMeta.types.namePlaceholder')}
            aria-label={t('channelMeta.types.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
          />
          <ColorSelect value={color} disabled={busy} onChange={setColor} />
          <Button type="submit" kind="primary" icon="plus" disabled={busy || !ChannelMeta.cleanName(name)}>
            {t('channelMeta.types.add')}
          </Button>
        </form>
      </div>
    </SettingsGroup>
  );
}
